import { useEffect, useRef } from 'react';
import {
  STELLEN, beimGehen, beimTippen, beimZurueck, nurZiffern, zeichenAn,
} from './codefelder.ts';

/**
 * Sechs Kästchen für den Code aus der Authenticator-App.
 *
 *   ORIGINALTEXT MARVIN (09.09.2026)
 *     „der code soll in 6 kästchen eingetragen werden, nicht in einem
 *      textfeld."
 *
 * Die Regeln stehen ohne JSX in `codefelder.ts` - hier steht nur, was man
 * sieht und anfassen kann. Drei Dinge, die dabei leicht schiefgehen:
 *
 *   1. **Ein Passwortspeicher oder ein Telefon fügt alle sechs Zeichen in EIN
 *      Kästchen.** Sie werden von dort an verteilt; ein Feld mit `maxLength=1`
 *      allein hätte fünf davon weggeworfen.
 *   2. **`autoComplete="one-time-code"` gehört an das ERSTE Kästchen und an
 *      kein zweites.** Sonst bietet iOS denselben Code sechsmal an.
 *   3. **Die Rücktaste muss löschen UND zurückgehen.** Wer nur den Sprung
 *      baut, löscht nie etwas - dann sind Kästchen unbedienbar.
 */
export function Codefelder({
  wert, setzen, abschicken, gesperrt = false, id = 'code',
}: {
  wert: string;
  setzen: (neu: string) => void;
  /** Wird gerufen, sobald die sechste Ziffer steht - MIT dem fertigen Code.
   *
   *  **DER WERT GEHT MIT, WEIL DER ZUSTAND DANEBEN NOCH DER ALTE IST.** Bis
   *  zum 09.09.2026 stand hier `() => void`, und der Aufrufer las den Code
   *  aus seinem eigenen Zustand: `setzen(...)` und `abschicken()` laufen im
   *  SELBEN Ereignis, und React setzt den Zustand erst danach. Der Server
   *  bekam damit fünf Ziffern statt sechs und antwortete „Dieser Code stimmt
   *  nicht" - auf einen Code, der stimmte.
   *
   *  Gefunden hat es Prüfstand 020, im Browser. Über HTTP ist es nicht zu
   *  sehen: dort schickt der Prüfstand den Code selbst, und der ist ganz. */
  abschicken?: (code: string) => void;
  gesperrt?: boolean;
  id?: string;
}) {
  const felder = useRef<Array<HTMLInputElement | null>>([]);

  // Die Schreibmarke steht beim Öffnen im ersten Kästchen: wer hier landet,
  // hat genau eine Aufgabe und soll nicht erst klicken müssen.
  useEffect(() => { felder.current[0]?.focus(); }, []);

  function anwenden(stand: { code: string; stelle: number }) {
    setzen(stand.code);
    felder.current[stand.stelle]?.focus();
    felder.current[stand.stelle]?.select();
  }

  return (
    <div className="zifferfelder" role="group" aria-label="Sechsstelliger Code">
      {Array.from({ length: STELLEN }, (_, i) => (
        <input
          key={i}
          id={i === 0 ? id : `${id}-${i + 1}`}
          ref={(e) => { felder.current[i] = e; }}
          className="zifferfeld"
          inputMode="numeric"
          /* `type="text"` und nicht `number`: ein Zahlenfeld bringt in jedem
             Browser seine eigenen Pfeilchen mit und lässt sich mit „e" und
             „+" füllen. Die Ziffertastatur holt `inputMode`. */
          type="text"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Stelle ${i + 1} von ${STELLEN}`}
          disabled={gesperrt}
          value={zeichenAn(wert, i)}
          onChange={(e) => {
            const stand = beimTippen(wert, i, e.target.value);
            anwenden(stand);
            if (/^\d{6}$/.test(stand.code)) abschicken?.(stand.code);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              anwenden(beimZurueck(wert, i));
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              anwenden(beimGehen(wert, i, -1));
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              anwenden(beimGehen(wert, i, 1));
            }
          }}
          onPaste={(e) => {
            /* EINGEFÜGT WIRD IMMER VON VORN. Wer sechs Ziffern in Kästchen 4
               einfügt, meint den ganzen Code und nicht die letzten drei. */
            e.preventDefault();
            const stand = beimTippen('', 0,
              nurZiffern(e.clipboardData.getData('text')));
            anwenden(stand);
            if (/^\d{6}$/.test(stand.code)) abschicken?.(stand.code);
          }}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
